import React, { useState, useEffect } from 'react';
import QRCode from 'react-qr-code';
import {
    FaTicketAlt,
    FaTimes,
    FaCalendarAlt,
    FaMapMarkerAlt,
    FaArrowLeft,
    FaQrcode,
    FaIdBadge,
    FaHistory
} from 'react-icons/fa';
import './TicketsModal.css';
import API_BASE_URL from '../../config/api';

const TicketsModal = ({ isOpen, onClose, userId }) => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('active'); // 'active', 'used', 'annual'
    const [selectedTicket, setSelectedTicket] = useState(null);

    useEffect(() => {
        if (isOpen && userId) {
            fetchTickets();
        }
    }, [isOpen, userId]);

    const fetchTickets = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/tickets/my-tickets`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setTickets(data);
            }
        } catch (err) {
            console.error('Error fetching tickets:', err);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const filteredTickets = tickets.filter(t => {
        const isAnnual = t.event.isAnnualPass;
        const isActive = t.status === 1;

        if (filter === 'annual') return isAnnual;
        if (filter === 'active') return isActive && !isAnnual;
        if (filter === 'used') return t.status === 2;
        return true;
    });

    const getStatusLabel = (status) => {
        switch (status) {
            case 1: return <span className="ticket-status-badge status-active">Ativo</span>;
            case 2: return <span className="ticket-status-badge status-used">Utilizado</span>;
            case 3: return <span className="ticket-status-badge status-expired">Cancelado</span>;
            default: return null;
        }
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const formatTime = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="tickets-modal-overlay">
            <div className="tickets-modal-container">
                <div className="tickets-modal-header">
                    <h2>
                        <FaTicketAlt style={{ color: '#003380' }} />
                        {selectedTicket ? 'O Seu Bilhete' : 'Os Meus Bilhetes'}
                    </h2>
                    <button className="tickets-modal-close" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                <div className="tickets-modal-content">
                    {loading ? (
                        <div className="empty-tickets">
                            <div className="spinner"></div>
                            <p>A carregar os seus bilhetes...</p>
                        </div>
                    ) : selectedTicket ? (
                        <div className="qr-display-container">
                            <div className="qr-card">
                                <QRCode
                                    value={selectedTicket.ticketCode}
                                    size={200}
                                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                                />
                            </div>
                            <div className="ticket-code-display">
                                <div className="code-value">
                                    {selectedTicket.ticketCode.match(/.{1,4}/g).join(' ')}
                                </div>
                            </div>
                            <div className="qr-info">
                                <h3>{selectedTicket.event.title}</h3>
                                {selectedTicket.event.isAnnualPass && (
                                    <p style={{ color: '#9333ea', fontWeight: 'bold' }}>
                                        <FaIdBadge /> Passe Anual - {selectedTicket.event.sportName}
                                    </p>
                                )}
                                <p>Apresente este código na entrada do recinto para validação.</p>

                                <div className="ticket-item" style={{ background: '#f8fafc', marginBottom: '24px' }}>
                                    <div className="ticket-details" style={{ textAlign: 'left' }}>
                                        <div className="ticket-meta">
                                            {!selectedTicket.event.isAnnualPass && (
                                                <span><FaCalendarAlt /> {formatDate(selectedTicket.event.startDateTime)}</span>
                                            )}
                                            {selectedTicket.event.location && <span><FaMapMarkerAlt /> {selectedTicket.event.location}</span>}
                                        </div>
                                    </div>
                                </div>

                                <button className="btn-back-list" onClick={() => {
                                    setSelectedTicket(null);
                                    fetchTickets();
                                }}>
                                    <FaArrowLeft /> Voltar à lista
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="tickets-filter-tabs">
                                <button
                                    className={`filter-tab ${filter === 'active' ? 'active' : ''}`}
                                    onClick={() => setFilter('active')}
                                >
                                    Próximos Jogos
                                </button>
                                <button
                                    className={`filter-tab ${filter === 'annual' ? 'active' : ''}`}
                                    onClick={() => setFilter('annual')}
                                >
                                    Passes Anuais
                                </button>
                                <button
                                    className={`filter-tab ${filter === 'used' ? 'active' : ''}`}
                                    onClick={() => setFilter('used')}
                                >
                                    Histórico
                                </button>
                            </div>

                            {filteredTickets.length > 0 ? (
                                <div className="tickets-list">
                                    {filteredTickets.map(ticket => (
                                        <div key={ticket.id} className="ticket-item">
                                            <div className="ticket-main-info">
                                                <div className={`ticket-icon ${ticket.event.isAnnualPass ? 'annual' : ''}`}>
                                                    {ticket.event.isAnnualPass ? <FaIdBadge /> : <FaTicketAlt />}
                                                </div>
                                                <div className="ticket-details">
                                                    <h4>{ticket.event.title}</h4>
                                                    {ticket.event.isAnnualPass && (
                                                        <div className="ticket-modality">
                                                            <FaIdBadge /> {ticket.event.sportName}
                                                        </div>
                                                    )}
                                                    <div className="ticket-meta">
                                                        {!ticket.event.isAnnualPass && (
                                                            <span><FaCalendarAlt /> {formatDate(ticket.event.startDateTime)}</span>
                                                        )}
                                                        {ticket.event.location && <span><FaMapMarkerAlt /> {ticket.event.location}</span>}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="ticket-item-actions">
                                                {getStatusLabel(ticket.status)}
                                                {(ticket.status === 1) && (
                                                    <button
                                                        className="btn-show-qr"
                                                        onClick={() => setSelectedTicket(ticket)}
                                                    >
                                                        <FaQrcode /> QR
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-tickets">
                                    <i className="fas fa-ticket-alt"></i>
                                    <p>Não foram encontrados bilhetes nesta categoria.</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TicketsModal;
